import React, { useMemo } from 'react';
import { Form, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FieldConfig, FormFieldProps } from '../types';
import MyInput from './MyInput';
import MyTextArea from './MyTextArea';
import MySelect from './MySelect';
import MyCascader from './MyCascader';
import MyDatePicker, { MyRangePicker } from './MyDatePicker';
import MyTimePicker, { MyTimeRangePicker } from './MyTimePicker';
import MyCheckbox from './MyCheckbox';
import MyRadio from './MyRadio';
import MySwitch from './MySwitch';
import MySlider from './MySlider';
import MyRate from './MyRate';
import MyUpload from './MyUpload';

/**
 * FormField component that renders individual form fields based on configuration
 */
const FormField: React.FC<FormFieldProps> = ({
  field,
  mode,
  value,
  onChange,
  disabled,
  readonly,
  formValues = {},
}) => {
  // Check if field should be visible
  const isVisible = useMemo(() => {
    if (field.hidden) return false;

    // Check mode visibility
    if (mode && field.visibleInModes && !field.visibleInModes.includes(mode)) {
      return false;
    }

    if (field.visible) {
      return field.visible(formValues);
    }
    return true;
  }, [field, mode, formValues]);

  // Render custom field if render function is provided
  if (field.render) {
    return field.render(field, value, onChange || (() => {}));
  }

  // Determine if field should be disabled
  const isDisabled = disabled || field.disabled || readonly;

  // Build label with tooltip
  const label = field.tooltip ? (
    <span>
      {field.label}{' '}
      <Tooltip title={field.tooltip}>
        <QuestionCircleOutlined className="form-field-tooltip-icon" />
      </Tooltip>
    </span>
  ) : (
    field.label
  );

  // Render appropriate component based on field type
  const renderFieldComponent = () => {
    const commonProps = {
      value,
      onChange,
      placeholder: field.placeholder,
      disabled: isDisabled,
      readonly,
      ...field.componentProps,
    };

    switch (field.type) {
      case 'input':
        return <MyInput {...commonProps} />;

      case 'password':
        return <MyInput {...commonProps} type="password" />;

      case 'email':
        return <MyInput {...commonProps} type="email" />;

      case 'number':
        return <MyInput {...commonProps} type="number" />;

      case 'phone':
        return <MyInput {...commonProps} type="phone" />;

      case 'textarea':
        return <MyTextArea {...commonProps} />;

      case 'select':
        return <MySelect {...commonProps} options={field.options} />;

      case 'multiselect':
        return <MySelect {...commonProps} options={field.options} mode="multiple" />;

      case 'cascader':
        return <MyCascader {...commonProps} options={field.cascaderOptions || field.options} />;

      case 'date':
        return <MyDatePicker {...commonProps} />;

      case 'daterange':
        return <MyRangePicker {...commonProps} />;

      case 'time':
        return <MyTimePicker {...commonProps} />;

      case 'timerange':
        return <MyTimeRangePicker {...commonProps} />;

      case 'checkbox':
        return <MyCheckbox {...commonProps} options={field.options} />;

      case 'radio':
        return <MyRadio {...commonProps} options={field.options} />;

      case 'switch':
        return <MySwitch {...commonProps} />;

      case 'slider':
        return <MySlider {...commonProps} />;

      case 'rate':
        return <MyRate {...commonProps} />;

      case 'upload':
        return <MyUpload {...commonProps} />;

      default:
        return <MyInput {...commonProps} />;
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Form.Item
      name={field.name}
      label={label}
      rules={field.rules}
      initialValue={field.defaultValue}
      help={field.help}
      className={field.className}
      labelCol={field.labelCol}
      wrapperCol={field.wrapperCol}
      style={field.formItemStyle}
    >
      {renderFieldComponent()}
    </Form.Item>
  );
};

export default FormField;
